import { useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Card, Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Bot } from "../api/types";

// Round 38 — real testing bot accounts (routes/admin/bots.js has the full
// reasoning: a bot is a completely ordinary profiles row, backed by a real
// auth.users row, distinguished only by is_bot=true). This page creates/
// lists/deletes them, and can drop them into a real room as genuine seat
// occupants (a real move_seat() call, the same one a human's seat-join
// uses) for testing room-occupancy UI/behavior without human testers.
export function BotManagementPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Bot>("/admin/bots");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { showToast } = useToast();

  // Create form
  const [maleCount, setMaleCount] = useState(0);
  const [femaleCount, setFemaleCount] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  // Room placement form
  const [roomId, setRoomId] = useState("");
  const [botCount, setBotCount] = useState(1);
  const [isPlacing, setIsPlacing] = useState(false);

  // Room removal form
  const [removeRoomId, setRemoveRoomId] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }

  async function createBots() {
    if (maleCount <= 0 && femaleCount <= 0) {
      showToast("Enter at least one male or female bot count", "error");
      return;
    }
    setIsCreating(true);
    try {
      const result = await apiRequest<{ createdCount: number; failedCount: number }>("/admin/bots", {
        method: "POST",
        body: { maleCount, femaleCount },
      });
      if (result.failedCount > 0) {
        showToast(`Created ${result.createdCount}, ${result.failedCount} failed — see console/audit log`, "error");
      } else {
        showToast(`Created ${result.createdCount} bot(s)`, "success");
      }
      setMaleCount(0);
      setFemaleCount(0);
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not create bots", "error");
    } finally {
      setIsCreating(false);
    }
  }

  async function confirmBulkDelete() {
    setIsDeleting(true);
    try {
      const result = await apiRequest<{ deleted: unknown[]; skipped: { reason: string }[] }>("/admin/bots", {
        method: "DELETE",
        body: { ids: Array.from(selected) },
      });
      if (result.skipped.length) {
        showToast(`Deleted ${result.deleted.length}, skipped ${result.skipped.length} (real gift history)`, "success");
      } else {
        showToast(`Deleted ${result.deleted.length} bot(s)`, "success");
      }
      setSelected(new Set());
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete selected bots", "error");
    } finally {
      setIsDeleting(false);
      setPendingBulkDelete(false);
    }
  }

  async function addToRoom() {
    const publicRoomId = Number(roomId);
    if (!publicRoomId || botCount <= 0) {
      showToast("Enter a valid Room ID and a positive bot count", "error");
      return;
    }
    setIsPlacing(true);
    try {
      const result = await apiRequest<{ placed: unknown[]; failed: unknown[] }>("/admin/bots/room-add", {
        method: "POST",
        body: { publicRoomId, botCount },
      });
      if (result.failed.length) {
        showToast(`Placed ${result.placed.length}, ${result.failed.length} failed`, "error");
      } else {
        showToast(`Placed ${result.placed.length} bot(s) in room ${publicRoomId}`, "success");
      }
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not add bots to room", "error");
    } finally {
      setIsPlacing(false);
    }
  }

  async function removeFromRoom() {
    const publicRoomId = Number(removeRoomId);
    if (!publicRoomId) {
      showToast("Enter a valid Room ID", "error");
      return;
    }
    setIsRemoving(true);
    try {
      const result = await apiRequest<{ removed: number }>("/admin/bots/room-remove", {
        method: "POST",
        body: { publicRoomId },
      });
      showToast(`Removed ${result.removed} bot(s) from room ${publicRoomId}`, "success");
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not remove bots from room", "error");
    } finally {
      setIsRemoving(false);
    }
  }

  const columns: Column<Bot>[] = [
    {
      header: "",
      className: "w-10",
      render: (row) => (
        <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelected(row.id)} className="h-4 w-4 rounded border-surface-border" />
      ),
    },
    {
      header: "Bot",
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar src={row.avatar_url} className="h-9 w-9 rounded-full object-cover" />
          <div>
            <p className="font-medium text-ink">{row.username}</p>
            <p className="text-xs text-ink-faint">ID: {row.user_id}</p>
          </div>
        </div>
      ),
    },
    { header: "Gender", render: (row) => <Badge tone={row.gender === "female" ? "gold" : "neutral"}>{row.gender ?? "—"}</Badge> },
    { header: "Country", render: (row) => row.country ?? "—" },
    { header: "Created", render: (row) => new Date(row.created_at).toLocaleDateString() },
  ];

  return (
    <AppShell title="Bot Management">
      <p className="mb-4 text-xs text-ink-faint">
        Bots created here are real accounts (a real auth user + profile, flagged is_bot) — not fake/mocked data. Adding
        a bot to a room places it in a real seat via the same server-side seat-claim every human join uses, so it shows
        up in real occupant counts everywhere the app reads them.
      </p>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Create Bots</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Male bots"
              type="number"
              min={0}
              value={maleCount}
              onChange={(e) => setMaleCount(Math.max(0, Number(e.target.value)))}
            />
            <Input
              label="Female bots"
              type="number"
              min={0}
              value={femaleCount}
              onChange={(e) => setFemaleCount(Math.max(0, Number(e.target.value)))}
            />
          </div>
          <Button className="mt-3" onClick={createBots} isLoading={isCreating}>
            Create Bots
          </Button>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-ink">Add Bots to Room</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Room ID (public)" placeholder="e.g. 1234567" value={roomId} onChange={(e) => setRoomId(e.target.value)} />
            <Input
              label="Number of bots"
              type="number"
              min={1}
              value={botCount}
              onChange={(e) => setBotCount(Math.max(1, Number(e.target.value)))}
            />
          </div>
          <Button className="mt-3" onClick={addToRoom} isLoading={isPlacing}>
            Add Bots to Room
          </Button>

          <div className="mt-4 border-t border-surface-border pt-4">
            <Input label="Room ID (public) to clear bots from" placeholder="e.g. 1234567" value={removeRoomId} onChange={(e) => setRemoveRoomId(e.target.value)} />
            <Button className="mt-3" variant="secondary" onClick={removeFromRoom} isLoading={isRemoving}>
              Remove Bots from Room
            </Button>
          </div>
        </Card>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">All Bots</h3>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface-card px-3 py-2">
            <span className="text-xs text-ink-muted">{selected.size} selected</span>
            <Button size="sm" variant="danger" onClick={() => setPendingBulkDelete(true)}>
              Delete Selected
            </Button>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mb-2 flex items-center gap-2 px-1">
          <input type="checkbox" checked={selected.size === rows.length} onChange={toggleSelectAll} className="h-4 w-4 rounded border-surface-border" />
          <span className="text-xs text-ink-faint">Select all on this page</span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No bots created yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <ConfirmDialog
        isOpen={pendingBulkDelete}
        onClose={() => setPendingBulkDelete(false)}
        onConfirm={confirmBulkDelete}
        isLoading={isDeleting}
        isDangerous
        title={`Delete ${selected.size} bot(s)?`}
        description="Permanently deletes these bot accounts (unless a bot has real gift history, in which case it's unseated and kept instead). This action is logged and cannot be undone."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
